import React, { useState } from "react";
import { connect } from "react-redux";

import cn from "classnames";
import { useIntl } from "react-intl";

import AnyColor from "@mapmycustomers/shared/types/AnyColor";
import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { EditableText } from "@mapmycustomers/ui";

import OrganizationSetting from "@app/enum/OrganizationSetting";
import ViewType from "@app/scene/dashboard/enum/ViewType";
import { addCard } from "@app/scene/dashboard/store/actions";
import { getBoards } from "@app/scene/dashboard/store/selectors";
import canEditBoard from "@app/scene/dashboard/utils/canEditBoard";
import {
  getCurrentUser,
  getOrganizationSettingValue,
  isCurrentUserManager,
  isCurrentUserOwner,
} from "@app/store/iam";
import { getTeams } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import Dashboard from "@app/types/dashboard/Dashboard";
import Scope from "@app/types/dashboard/Scope";
import { complementFileUrl } from "@app/util/file";

import AddCardWizard from "../../addEditCard/AddCardWizard";
import useAddCard from "../../DashboardContent/util/useAddCard";

import BaseHeader from "./BaseHeader";
import EllipsisMenu from "./EllipsisMenu";
import styles from "./HeaderCommon.module.scss";
import Image from "./Image";

interface TeamHeaderContentProps {
  auto?: boolean;
  colors?: AnyColor[];
  editable?: boolean;
  onGradientChange?: (
    settings: Partial<Pick<Dashboard["settings"], "gradient" | "gradientFromImage">>
  ) => void;
  teamId: Team["id"];
  view: ViewType;
}

interface Props extends TeamHeaderContentProps {
  boards: Dashboard[];
  currentUser: User;
  isManager?: boolean;
  isOwner?: boolean;
  onAddCard: typeof addCard.request;
  organizationLogoUrl?: string;
  scope: Scope;
  teams: Team[];
}

const TeamHeaderContent: React.FC<Props> = ({
  auto,
  boards,
  colors,
  currentUser,
  editable,
  isManager,
  isOwner,
  onAddCard,
  onGradientChange,
  organizationLogoUrl,
  scope,
  teamId,
  teams,
  view,
}) => {
  const intl = useIntl();
  const logoUrl = complementFileUrl(organizationLogoUrl);

  const team = teams.find(({ id }) => id === teamId);
  const [editing, setEditing] = useState(false);
  const [addCardWizardVisible, showAddCardWizard, hideAddCardWizard] = useBoolean();

  const canAddCharts = canEditBoard(currentUser, scope, "add");
  const handleAddChart = useAddCard(boards, currentUser, scope, onAddCard);

  if (!team) {
    return null;
  }

  return (
    <BaseHeader
      auto={auto}
      colors={colors}
      editable={editable}
      logoUrl={logoUrl}
      onGradientChange={onGradientChange}
    >
      {logoUrl ? <Image alt="organization logo" className={styles.image} src={logoUrl} /> : null}
      <div className={styles.info}>
        <EditableText
          className={cn(styles.name, { [styles.nameEditing]: editing })}
          editable={isOwner || isManager}
          editing={editing}
          onEditingChange={setEditing}
          required
          value={team.name}
        />
        {team.users.length ? (
          <span>
            {intl.formatMessage(
              {
                id: "dashboard.header.team.numberOfUsers",
                defaultMessage: "{count} {count, plural, one {User} other {Users}}",
                description: "Number of team users chip in Dashboard's header for the team scope",
              },
              {
                count: team.users.length,
              }
            )}
          </span>
        ) : null}
      </div>
      {addCardWizardVisible && (
        <AddCardWizard onAddCard={handleAddChart} onHide={hideAddCardWizard} scope={scope} />
      )}
      {canAddCharts && view === ViewType.DASHBOARD && (
        <EllipsisMenu
          items={[
            {
              key: "addChart",
              label: intl.formatMessage({
                id: "dashboard.header.team.menu.addChart",
                defaultMessage: "Add Chart",
                description: "Add chart menu item in Dashboard's header for the team scope",
              }),
              onClick: showAddCardWizard,
            },
          ]}
        />
      )}
    </BaseHeader>
  );
};

const mapStateToProps = (state: RootState) => ({
  boards: getBoards(state),
  currentUser: getCurrentUser(state)!,
  isManager: isCurrentUserManager(state),
  isOwner: isCurrentUserOwner(state),
  organizationLogoUrl: getOrganizationSettingValue(state)(OrganizationSetting.LOGO),
  teams: getTeams(state),
});

const mapDispatchToProps = {
  onAddCard: addCard.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(TeamHeaderContent);
